import React from 'react';


import { Stack } from 'ui/stack';

import { Input } from 'ui/input';

interface EditableDiscountRateCellProps {
  value: number;
  onChange: (value: number) => void;
  isDirty?: boolean;
  disabled?: boolean;
  originalValue?: number | null;
}

const PERCENT_DECIMALS = 1;
const PERCENT_ROUNDING_FACTOR = 10 ** PERCENT_DECIMALS;

const toPercentString = (fraction: number): string => {
  const percent =
    Math.round(fraction * 100 * PERCENT_ROUNDING_FACTOR) /
    PERCENT_ROUNDING_FACTOR;

  return percent % 1 === 0
    ? String(percent)
    : percent.toFixed(PERCENT_DECIMALS);
};

const EditableDiscountRateCell = ({
  value,
  onChange,
  isDirty,
  disabled,
  originalValue,
}: EditableDiscountRateCellProps) => {
  const [localValue, setLocalValue] = React.useState(toPercentString(value));
  const [isFocused, setIsFocused] = React.useState(false);
  const showOriginal = originalValue != null && Number.isFinite(originalValue);

  React.useEffect(() => {
    if (!isFocused) {
      setLocalValue(toPercentString(value));
    }
  }, [value, isFocused]);

  const handleCommit = () => {
    setIsFocused(false);
    const parsed = Number(localValue);

    if (Number.isNaN(parsed)) {
      setLocalValue(toPercentString(value));

      return;
    }

    const fraction = parsed / 100;

    if (fraction !== value) {
      onChange(fraction);
    } else {
      setLocalValue(toPercentString(fraction));
    }
  };

  return (
    <Stack sx={{ gap: 0.5 }}>
      <Input
        value={localValue}
        placeholder="0"
        disabled={disabled}
        endAdornment="%"
        dirty={isDirty}
        onChange={(e) => setLocalValue(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={handleCommit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
        size="medium"
        sx={{ width: 80 }}
        aria-label="Tuition discount rate override"
        data-testid="editable-discount-rate-input"
      />
      {showOriginal && (
        <span className="text-xs text-gray-500">
          Original:{' '}
          <span className="font-medium">{toPercentString(originalValue)}%</span>
        </span>
      )}
    </Stack>
  );
};

export default EditableDiscountRateCell;
